import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useLang } from "@/contexts/LanguageContext";
import { toast } from "sonner";

function timeAgo(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "ahora";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString();
}

// Web Audio API beep — no external file. Two-tone alert (high → low) so it's
// perceptibly different from generic browser notifications.
function playAlertSound() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;
    [
      { freq: 880, start: 0, dur: 0.12 },
      { freq: 660, start: 0.14, dur: 0.16 },
    ].forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + start);
      gain.gain.exponentialRampToValueAtTime(0.18, now + start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.02);
    });
    // Auto-close ctx after sequence finishes
    setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 500);
  } catch { /* silent */ }
}

export default function NotificationBell() {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const lastSeenIdsRef = useRef(new Set());
  const primedRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const [n, c] = await Promise.all([
        api.get("/notifications"),
        api.get("/notifications/unread-count"),
      ]);
      const nextItems = n.data || [];
      const nextCount = c.data.count || 0;

      // Detect brand-new unread items (that arrived since last poll). We skip
      // the first fetch after mount so we don't fire an alert for the historic
      // backlog the agent already knew about.
      if (primedRef.current) {
        const newHigh = nextItems.filter(
          (x) => !x.read && x.priority === "high" && !lastSeenIdsRef.current.has(x.id)
        );
        if (newHigh.length > 0) {
          playAlertSound();
          const n0 = newHigh[0];
          const target = n0.action_url
            || (n0.conversation_id ? `/inbox?conv=${n0.conversation_id}` : null)
            || (n0.lead_id ? `/leads?id=${n0.lead_id}` : null);
          toast.error(n0.title, {
            description: n0.message,
            duration: 8000,
            action: target
              ? {
                  label: lang === "es" ? "Abrir" : "Open",
                  onClick: () => nav(target),
                }
              : undefined,
          });
        }
      }
      lastSeenIdsRef.current = new Set(nextItems.map((x) => x.id));
      primedRef.current = true;
      setItems(nextItems);
      setCount(nextCount);
    } catch { /* ignore */ }
  }, [lang, nav]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [refresh]);

  const openNotif = async (n) => {
    if (!n.read) {
      await api.patch(`/notifications/${n.id}/read`);
      refresh();
    }
    setOpen(false);
    // Fallback deep-links: even if action_url is missing (older data), we can
    // route to the correct page from the linked entity ids.
    let target = n.action_url;
    if (!target && n.conversation_id) target = `/inbox?conv=${n.conversation_id}`;
    else if (!target && n.lead_id) target = `/leads?id=${n.lead_id}`;
    if (target) nav(target);
  };

  const readAll = async () => {
    await api.post("/notifications/read-all");
    refresh();
  };

  const hasHighUnread = items.some((n) => !n.read && n.priority === "high");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={`relative ${hasHighUnread ? "animate-[bell-shake_0.9s_ease-in-out_infinite]" : ""}`}
          data-testid="notif-bell"
        >
          <Bell
            size={18}
            weight={count > 0 ? "fill" : "regular"}
            className={hasHighUnread ? "text-rose-500" : ""}
          />
          {count > 0 && (
            <span
              className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-white text-[10px] font-bold flex items-center justify-center ${
                hasHighUnread ? "bg-rose-500 animate-pulse ring-2 ring-rose-500/40" : "bg-red-500"
              }`}
              data-testid="notif-count"
            >
              {count > 9 ? "9+" : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notif-panel">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div className="overline flex items-center gap-2">
            {hasHighUnread && <WarningCircle size={14} weight="fill" className="text-rose-500 animate-pulse"/>}
            {lang === "es" ? "Notificaciones" : "Notifications"}
          </div>
          {count > 0 && (
            <button onClick={readAll} className="text-xs text-primary hover:underline" data-testid="notif-read-all">
              {lang === "es" ? "Marcar todas leídas" : "Mark all read"}
            </button>
          )}
        </div>
        <ScrollArea className="max-h-96">
          {items.length === 0 && (
            <div className="p-8 text-center text-xs text-muted-foreground">
              {lang === "es" ? "Sin notificaciones" : "No notifications"}
            </div>
          )}
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => openNotif(n)}
              data-testid={`notif-${n.id}`}
              className={`w-full text-left p-3 border-b border-border transition-colors hover:bg-secondary ${
                !n.read ? (n.priority === "high" ? "bg-rose-500/10" : "bg-accent/40") : ""
              }`}
            >
              <div className="flex items-start gap-2.5">
                <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                  n.priority === "high" ? "bg-red-500 ring-2 ring-rose-500/40 animate-pulse" : n.priority === "medium" ? "bg-amber-500" : "bg-slate-400"
                }`}/>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{n.title}</div>
                  <div className="text-xs text-muted-foreground truncate">{n.message}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(n.created_at)}</div>
                </div>
                {n.read && <CheckCircle size={14} className="text-muted-foreground shrink-0"/>}
              </div>
            </button>
          ))}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
